import { LitElement, html, nothing, TemplateResult } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FontSizes, FontWeights } from '../../../types/common/types';
import {
  CONNECTION_FILE_REMOTE_OPERATION_OPTIONS,
  ConnectionOptions,
  DetectionRuleType, FILE_OPERATION_OPTIONS,
  FileOptions,
  GridType, REGISTRY_OPERATION_OPTIONS, RegistryOptions, ruleFields,
  RuleType,
  Severity,
} from '../../customRulesGrid/types';
import { translateFn } from '../../../utils/translator.helper';
import { GzLabeledContentWebComponent } from '../../../ui/gzLabeledContent';
import { htmlEncode } from '../../../utils/escape.utils';
import { YARA_CODE_EDITOR_OPTIONS } from '../../addRule/component/yaraDefinition/types';
import { GzQueryInputWebComponent } from '../../../ui/gzQueryInput';
import { copyToClipboard } from '../../../utils/htmlElement.utils';
import { GzTooltip } from '../../../ui/gzTooltip';
import { YARA_ID } from '@nirvana/shared/code-editor';
import { Feature } from '@nirvana/shared/constants';
import {
  RULE_FIELDS_NAME_MAPPING,
  RULE_OUTCOME_CONFIG,
  RULE_RELATIONS_MAPPING
} from '../index';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import { MINIMUM_OF_CRITERIA_DISPLAYED, SHOW_BUTTONS } from '../types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../ui/gzButton/types';
import { AutomaticAction } from '../../addRule/component/ruleAutomaticActions/types';

/**
 * @summary Type here the description of this component
 * @tag custom-rules-rule-outcome
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('custom-rules-rule-outcome')
export class CustomRulesRuleOutcomeWebComponent extends LitElement {
  /**
   * See https://lit-element.polymer-project.org/guide/properties
   * Write here the description of this field
   */
  @property({ type: Object, attribute: false, reflect: false })
  rule;

  @property({ type: String, attribute: false, reflect: false })
  gridType;

  @property({ type: Boolean })
  isGZ = false;

  @query('gz-search-query')
  searchQuery: GzQueryInputWebComponent;

  @property({ type: Boolean, attribute: false, reflect: false })
  displayDoTheFollowingSection = true;

  @property({ type: Boolean, attribute: false, reflect: false })
  displayShowButtons = false;

  @state()
  private whichButtonShouldBeDisplayed = SHOW_BUTTONS.SHOW_MORE;

  @state()
  private tooltipShown;

  FILE_OPERATION_MAPPINGS = {
    [FILE_OPERATION_OPTIONS.CREATE]: translateFn('Create'),
    [FILE_OPERATION_OPTIONS.READ]: translateFn('Read'),
    [FILE_OPERATION_OPTIONS.WRITE]: translateFn('Write'),
    [FILE_OPERATION_OPTIONS.MOVE]: translateFn('Move'),
    [FILE_OPERATION_OPTIONS.RENAME]: translateFn('Rename'),
    [FILE_OPERATION_OPTIONS.COPY]: translateFn('Copy'),
  }

  CONNECTION_FILE_REMOTE_OPERATION_MAPPINGS = {
    [CONNECTION_FILE_REMOTE_OPERATION_OPTIONS.CREATE]: translateFn('Create'),
    [CONNECTION_FILE_REMOTE_OPERATION_OPTIONS.DELETE]: translateFn('Delete'),
    [CONNECTION_FILE_REMOTE_OPERATION_OPTIONS.READ]: translateFn('Read'),
    [CONNECTION_FILE_REMOTE_OPERATION_OPTIONS.WRITE]: translateFn('Write'),
    [CONNECTION_FILE_REMOTE_OPERATION_OPTIONS.MOVE]: translateFn('Move'),
  }

  REGISTRY_OPERATION_MAPPINGS = {
    [REGISTRY_OPERATION_OPTIONS.KEY_CREATED]: translateFn('Key created'),
    [REGISTRY_OPERATION_OPTIONS.KEY_DELETED]: translateFn('Key deleted'),
    [REGISTRY_OPERATION_OPTIONS.KEY_RENAMED]: translateFn('Key renamed'),
    [REGISTRY_OPERATION_OPTIONS.VALUE_CREATED]: translateFn('Value created'),
    [REGISTRY_OPERATION_OPTIONS.VALUE_DELETED]: translateFn('Value deleted'),
    [REGISTRY_OPERATION_OPTIONS.VALUE_WRITTEN]: translateFn('Value written'),
  }

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mouseleave', this.handleTooltipMouseLeave);
  }

  disconnectedCallback(): void {
    this.removeEventListener('mouseleave', this.handleTooltipMouseLeave);
    super.disconnectedCallback();
  }

  render() {
    return html`
      <gz-title
        fontSize="${FontSizes.PARAGRAPH_11}"
        fontWeight="${FontWeights.SEMIBOLD}"
        class="section-title text-uppercase mb-2"
        >${translateFn('Rule outcome')}
      </gz-title>

      ${this.gridType === GridType.DETECTION && this.rule.type === RuleType.YARA
        ? this.renderYaraRule()
        : this.renderDefaultRule()}
      ${this.displayDoTheFollowingSection ? this.renderDoTheFollowingSection() : nothing}
    `;
  }

  private renderDoTheFollowingSection(): TemplateResult {
    const enabledAutomaticActions = this.rule.automaticActions?.filter(automaticAction => automaticAction.enabled);

    return html`
      <gz-labeled-content class="wrapped mt-2" label=${translateFn('Do the following')} stacked>
        ${this.renderDoTheFollowingText()}
        ${this.rule.generateRca && this.rule.enableAutomaticActions && enabledAutomaticActions.length
          ? this.renderAutomaticActions(enabledAutomaticActions)
          : nothing}
      </gz-labeled-content>
      ${this.renderViewAlerts(this.rule._id)}${this.renderViewIncidents(this.rule._id)}
    `;
  }

  private renderAutomaticActions(enabledAutomaticActions: AutomaticAction[]): TemplateResult {
    return html`
      <gz-text class="automatic-actions-text" fontWeight="${FontWeights.NORMAL}">
        ${translateFn('Take the following automatic actions in order:')}
      </gz-text>
      ${this.renderActionsInOrder(enabledAutomaticActions)}
    `;
  }

  private renderActionsInOrder(enabledAutomaticActions: AutomaticAction[]): GzLabeledContentWebComponent {
    const translator = BaseWebComponentTranslator.getInstance();
    const actionsInOrder = new GzLabeledContentWebComponent();
    actionsInOrder.label = translateFn('Automatic actions');
    actionsInOrder.className = 'actions-in-order';
    actionsInOrder.innerText = enabledAutomaticActions
      .map(automaticActions => translator.translateCustomRulesAutomaticActionsType(automaticActions.type))
      .join(', ');
    return actionsInOrder;
  }

  private renderViewAlerts(ruleId: string) {
    if (!this.hasAdvancedSearchFeature()) {
      return nothing;
    }

    const link = `/#!/xdr/${this.gridType === GridType.DETECTION ? 'rule' : 'exclusion'}/${ruleId}`;
    return html` <gz-link class="text-uppercase mt-1 mb-1 view-alerts" href="${link}" fontSize="11">
      ${translateFn('View alerts')}
    </gz-link>`;
  }

  private renderViewIncidents(ruleId: string) {
    if (this.gridType !== GridType.DETECTION) {
      return nothing;
    }

    const link = `/#!/incidents/filter/rule/${ruleId}`;
    return html`
      ${this.renderSeparator()}
      <gz-link class="text-uppercase view-incidents" href="${link}" fontSize="11">
        ${translateFn('View incidents')}
      </gz-link>
    `;
  }

  private renderSeparator() {
    return this.hasAdvancedSearchFeature() ? html`<span class="separator"></span>` : nothing;
  }

  private renderYaraRule() {
    return html` <gz-title class="section-title" fontSize="12" fontWeight="500"
        >${translateFn('In case this happens') + ':'}
      </gz-title>
      <div class="mt-1 d-flex justify-end">
        <gz-button
          @click="${this.handleCopyButton}"
          class="pb-1"
          type="outline"
          color="primary"
          buttonHeight="small"
          buttonFontSize="small"
          lowercase="true"
          >${translateFn('Copy to clipboard')}
        </gz-button>
      </div>
      <gz-search-query .queryInputPlaceholder=${translateFn('Type here')} .compactMode=${true}></gz-search-query>`;
  }

  private renderDefaultRule() {
    return html` <gz-labeled-content
      class="wrapped"
      label=${translateFn('In case this happens' + ':')}
      labelFontWeight="500"
      stacked
      >${this.renderInCaseThisHappens()}
    </gz-labeled-content>`;
  }

  private async handleCopyButton(event: Event) {
    const codeEditorValue = await this.searchQuery.getValue();
    if (!codeEditorValue.raw) {
      return;
    }
    copyToClipboard(codeEditorValue.raw, true);
    this.handleTooltip(event);
  }

  private handleTooltip(event) {
    GzTooltip.show(translateFn('Copied'), event);

    this.tooltipShown = setTimeout(() => {
      GzTooltip.hide();
    }, 2000);
  }

  private handleTooltipMouseLeave(): void {
    clearTimeout(this.tooltipShown);
    GzTooltip.hide();
  }

  constructor() {
    super();

    this.updateComplete.then(() => {
      if (!this.searchQuery) {
        return;
      }

      this.searchQuery.setInputRows(10, 10);
      this.searchQuery.setupCodeEditor(YARA_ID, YARA_CODE_EDITOR_OPTIONS.languageOptions, {
        ...YARA_CODE_EDITOR_OPTIONS.editorOptions,
        theme: 'yaraTheme',
        readOnly: true,
        glyphMargin: false,
      });

      this.searchQuery.setValue(this.rule.query.yara);
    });
  }

  private getFieldsMapping(queryType) {
    const ruleOutcome = RULE_OUTCOME_CONFIG[queryType];

    let fields = Object.entries(RULE_FIELDS_NAME_MAPPING[queryType]).reduce((acc, [key, value]) => {
      return {
        ...acc,
        [`${ruleOutcome.prefix}.${key}`]: value,
      };
    }, {});

    if (ruleOutcome.parentPrefix) {
      fields = Object.entries(RULE_FIELDS_NAME_MAPPING[ruleOutcome.parentType]).reduce((acc, [key, value]) => {
        return {
          ...acc,
          [`${ruleOutcome.prefix}.${ruleOutcome.parentPrefix}.${key}`]: `${ruleOutcome.labelPrefix} ${value}`,
        };
      }, fields);
    }

    return fields;
  }

  private renderInCaseThisHappens() {
    const ruleQuery = this.rule.query;

    const fields = this.getFieldsMapping(ruleQuery.type);

    const matchingCriteriaWebComponent = new GzLabeledContentWebComponent();
    matchingCriteriaWebComponent.stacked = true;
    matchingCriteriaWebComponent.label = translateFn('A {TYPE} matching the following criteria', {
      TYPE: ruleQuery.type,
    });
    let matchingCriteria = '';

    let numberOfRulesToDisplay = ruleQuery.rules.length;
    if (
      this.displayShowButtons &&
      ruleQuery.rules.length > MINIMUM_OF_CRITERIA_DISPLAYED &&
      this.whichButtonShouldBeDisplayed === SHOW_BUTTONS.SHOW_MORE
    ) {
      numberOfRulesToDisplay = MINIMUM_OF_CRITERIA_DISPLAYED;
    }
    // append description for rules
    for (let index = 0; index < numberOfRulesToDisplay; index++) {
      const rule = ruleQuery.rules[index];
      const ruleField = rule['field'];
      const ruleRelation = rule['relation'];

      const valueMapping = this.getValueMappings(ruleQuery.type, rule);
      const criteriaRules = valueMapping.join(' OR ');

      const label = `${fields[ruleField]} ${RULE_RELATIONS_MAPPING[ruleRelation]}`;
      matchingCriteria += `<gz-labeled-content label="${label}">
                              <gz-tooltip class="tooltip" tooltipText="${htmlEncode(criteriaRules)}">
                                ${htmlEncode(criteriaRules)}
                              </gz-tooltip>
                            </gz-labeled-content>`;
    }

    // when query has detection, then append detection description
    if (ruleQuery.detection) {
      matchingCriteria += `<gz-labeled-content label="${translateFn('Alert name is')}">
                              <gz-tooltip class="tooltip" tooltipText="${htmlEncode(ruleQuery.detection)}">
                                ${htmlEncode(ruleQuery.detection)}
                              </gz-tooltip>
                           </gz-labeled-content>`;
    }
    matchingCriteriaWebComponent.innerHTML = matchingCriteria;

    if (this.displayShowButtons && ruleQuery.rules.length > 3) {
      matchingCriteriaWebComponent.appendChild(this.createShowButton());
    }

    return matchingCriteriaWebComponent;
  }

  private getValueMappings(ruleQueryType: DetectionRuleType, rule) {
    const ruleField = rule['field'];
    let ruleValue = rule['value'];
    ruleValue = this.getRuleValueLabels(ruleQueryType, ruleField, ruleValue);
    return ruleValue;
  }

  private getRuleValueLabels(ruleQueryType: DetectionRuleType, ruleField, ruleValue) {
    switch (ruleQueryType) {
      case DetectionRuleType.FILE:
        if (ruleField === ruleFields[DetectionRuleType.FILE][FileOptions.OPERATION]) {
          return ruleValue.map(value => this.FILE_OPERATION_MAPPINGS[value]);
        } else {
          return ruleValue;
        }
      case DetectionRuleType.CONNECTION:
        if (ruleField === ruleFields[DetectionRuleType.CONNECTION][ConnectionOptions.FILE_REMOTE_OPERATION]) {
          return ruleValue.map(value => this.CONNECTION_FILE_REMOTE_OPERATION_MAPPINGS[value]);
        } else {
          return ruleValue;
        }
      case DetectionRuleType.REGISTRY:
        if (ruleField === ruleFields[DetectionRuleType.REGISTRY][RegistryOptions.OPERATION]) {
          return ruleValue.map(value => this.REGISTRY_OPERATION_MAPPINGS[value]);
        } else {
          return ruleValue;
        }

      default:
        return ruleValue;
    }
  }

  private createShowButton(): GzButtonWebComponent {
    const button = new GzButtonWebComponent();
    button.type = ButtonTypes.QUIET;
    button.lowercase = true;
    button.buttonFontSize = ButtonFontSizes.SMALL;
    button.buttonHeight = ButtonHeights.SMALL;
    switch (this.whichButtonShouldBeDisplayed) {
      case SHOW_BUTTONS.SHOW_MORE:
        button.addEventListener('click', this.handleShowMore.bind(this));
        button.innerText = translateFn('Show more');
        break;
      case SHOW_BUTTONS.SHOW_LESS:
        button.addEventListener('click', this.handleShowLess.bind(this));
        button.innerText = translateFn('Show less');
        break;
    }
    return button;
  }

  private handleShowLess(): void {
    this.whichButtonShouldBeDisplayed = SHOW_BUTTONS.SHOW_MORE;
  }

  private handleShowMore(): void {
    this.whichButtonShouldBeDisplayed = SHOW_BUTTONS.SHOW_LESS;
  }

  private renderDoTheFollowingText() {
    if (this.gridType === GridType.EXCLUSION) {
      return this.isGZ
        ? translateFn('Stop generating incidents.')
        : translateFn('Save all events, but stop generating incidents.');
    }

    if (!this.rule.generateRca) {
      switch (this.rule.severity) {
        case Severity.LOW:
          return translateFn('Generate an alert with Low severity.');
        case Severity.MEDIUM:
          return translateFn('Generate an alert with Medium severity.');
        case Severity.HIGH:
          return translateFn('Generate an alert with High severity.');
        default:
          return translateFn('Generate an alert with N/A severity.');
      }
    }

    switch (this.rule.severity) {
      case Severity.LOW:
        return translateFn('Generate an alert with Low severity and display it in an incident.');
      case Severity.MEDIUM:
        return translateFn('Generate an alert with Medium severity and display it in an incident.');
      case Severity.HIGH:
        return translateFn('Generate an alert with High severity and display it in an incident.');
      default:
        return translateFn('Generate an alert with N/A severity and display it in an incident.');
    }
  }

  private hasAdvancedSearchFeature(): boolean {
    return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EDR_HISTORICAL_SEARCH]);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'custom-rules-rule-outcome': CustomRulesRuleOutcomeWebComponent;
  }
}
